/**
 * Active Directory Icons Registry
 *
 * Provides Active Directory object icons from local SVG assets.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import AdCertificateAuthority from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-certificate-authority.svg?react';
import AdComputer from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-computer.svg?react';
import AdDomain from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-domain.svg?react';
import AdDomainController from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-domain-controller.svg?react';
import AdForest from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-forest.svg?react';
import AdGroup from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-group.svg?react';
import AdGroupPolicy from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-group-policy.svg?react';
import AdOrganizationalUnit from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-organizational-unit.svg?react';
import AdServiceAccount from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-service-account.svg?react';
import AdSite from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-site.svg?react';
import AdTrust from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-trust.svg?react';
import AdUser from '@/sections/attacks/attackPaths/components/icons/bundles/ad/ad-user.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { AD_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type AdIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from AD_ICONS names to SVGR components.
 */
const adIconComponents: Partial<
  Record<(typeof AD_ICONS)[number], AdIconComponent>
> = {
  DomainController: AdDomainController,
  Forest: AdForest,
  Domain: AdDomain,
  OrganizationalUnit: AdOrganizationalUnit,
  User: AdUser,
  Computer: AdComputer,
  Group: AdGroup,
  GroupPolicy: AdGroupPolicy,
  CertificateAuthority: AdCertificateAuthority,
  ServiceAccount: AdServiceAccount,
  Trust: AdTrust,
  Site: AdSite,
};

/**
 * Human-readable labels for AD icons.
 */
const adIconLabels: Partial<Record<(typeof AD_ICONS)[number], string>> = {
  DomainController: 'AD Domain Controller',
  Forest: 'AD Forest',
  Domain: 'AD Domain',
  OrganizationalUnit: 'AD Organizational Unit',
  User: 'AD User',
  Computer: 'AD Computer',
  Group: 'AD Group',
  GroupPolicy: 'AD Group Policy',
  CertificateAuthority: 'AD Certificate Authority',
  ServiceAccount: 'AD Service Account',
  Trust: 'AD Trust',
  Site: 'AD Site',
};

// ============================================================================
// AD Renderer Implementation
// ============================================================================

/**
 * Active Directory icon renderer for the attack graph icon system.
 */
const adRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = adIconComponents[name as (typeof AD_ICONS)[number]];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (AD_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = adIconComponents[name as (typeof AD_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedAdIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    AD_ICONS.map(name => {
      const IconComponent = adIconComponents[name];
      return {
        id: `ad:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: adIconLabels[name] ?? name,
        category: 'ad',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('ad', adRenderer);

export { adRenderer };
